import {
    Checkbox as AnvilCheckbox,
    CheckboxProps as AnvilCheckboxProps,
    FieldMessage,
} from '@servicetitan/anvil2';
import { Controller, get, useFormContext } from 'react-hook-form';
import { useId } from 'react';

type CheckboxProps = Omit<AnvilCheckboxProps, 'onChange'> & {
    name: string;
    onChange?: (checked: boolean) => void;
    valueFromChecked?: (checked: boolean) => unknown;
};

export const Checkbox = ({
    onChange,
    valueFromChecked,
    error: errorProp,
    ...props
}: CheckboxProps) => {
    const {
        control,
        setValue,
        trigger,
        formState: { errors },
    } = useFormContext();
    const error = get(errors, props.name);
    const errorMessageId = useId();

    return (
        <Controller
            name={props.name}
            control={control}
            render={({ field: { value, onChange: fieldOnChange, ...field } }) => {
                const checkbox = (
                    <AnvilCheckbox
                        {...props}
                        {...field}
                        checked={Boolean(value)}
                        error={error != null || errorProp}
                        aria-errormessage={
                            error != null ? errorMessageId : props['aria-errormessage']
                        }
                        onChange={e => {
                            const checked = e?.target.checked ?? false;
                            const nextValue = valueFromChecked
                                ? valueFromChecked(checked)
                                : checked;
                            if (checked) {
                                fieldOnChange(nextValue);
                            } else {
                                // Defer unchecked validation until blur or submit to avoid eager feedback.
                                setValue(props.name, nextValue, { shouldDirty: true });
                            }
                            if (checked && error != null) {
                                trigger(props.name).catch(() => undefined);
                            }
                            onChange?.(checked);
                        }}
                    />
                );

                return (
                    <>
                        {checkbox}
                        {error != null && (
                            <div
                                style={{
                                    marginBlockStart: 'var(--a2-size-1, 0.25rem)',
                                    marginInlineStart: 'var(--a2-size-8, 2rem)',
                                }}
                            >
                                <FieldMessage id={errorMessageId} error={error.message} />
                            </div>
                        )}
                    </>
                );
            }}
        />
    );
};
